/**
 * AIChatPage — Full-screen multi-turn AI food discovery chat.
 * Features: session sidebar, result cards, follow-up chips, new/resume chats.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageSquare, Send, Plus, Trash2, ChevronRight, Sparkles, ArrowLeft,
  Star, MapPin, Clock, ChefHat, UtensilsCrossed, ShoppingBag,
  Navigation2, PanelLeftClose, PanelLeftOpen, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  dine_out: { label: 'Dine Out', icon: UtensilsCrossed, color: '#FF6B35' },
  order_in: { label: 'Order In', icon: ShoppingBag, color: '#3B82F6' },
  cook_at_home: { label: 'Cook It', icon: ChefHat, color: '#10B981' },
};

const STARTERS = [
  "Best pizza near me under $20",
  "Romantic dinner for two",
  "Quick healthy lunch",
  "Late-night street food",
  "Vegan brunch spots",
  "Show me something I've never tried",
  "Family dinner, kid-friendly",
  "Comfort food for a rainy day",
];

/* ── Mini result card for inline chat ──────────────────────────── */
const ChatResultCard = ({ r, idx }) => {
  const cat = CATEGORY_META[r.category] || CATEGORY_META.dine_out;
  const CatIcon = cat.icon;
  const mapQuery = `${r.venue_name || r.name} ${r.suburb || ''}`.trim();
  return (
    <div className="shrink-0 w-64 bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`chat-result-card-${idx}`}>
      {r.image ? (
        <div className="relative h-32 overflow-hidden">
          <img src={r.image} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: cat.color }}>
            <CatIcon className="w-3 h-3" />{cat.label}
          </span>
          {r.open_now && <span className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/90 text-white">Open</span>}
          <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full">
            <Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" />
            <span className="text-white text-[11px] font-bold">{r.rating?.toFixed?.(1) || '4.5'}</span>
            <span className="text-white/40">·</span>
            <span className="text-white text-[11px]">{r.price || '$$'}</span>
          </div>
        </div>
      ) : (
        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
          target="_blank" rel="noopener noreferrer"
          className="h-32 bg-gradient-to-br from-slate-50 to-orange-50 flex flex-col items-center justify-center hover:from-slate-100 hover:to-orange-100 transition-all">
          <Navigation2 className="w-6 h-6 text-[#FF6B35] mb-1" />
          <span className="text-[11px] font-semibold text-gray-700">Get Directions</span>
        </a>
      )}
      <div className="p-3">
        <h4 className="font-bold text-gray-900 text-xs truncate">{r.name}</h4>
        {r.venue_name && r.venue_name !== r.name && (
          <p className="text-[10px] text-gray-500 truncate">{r.venue_name}</p>
        )}
        {r.description && <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">{r.description}</p>}
        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-400">
          {r.suburb && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{r.suburb}</span>}
          {r.distance_km != null && <span>{r.distance_km} km</span>}
          {r.prep_time && <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{r.prep_time}</span>}
        </div>
        {r.why_this && <p className="text-[10px] text-[#FF6B35] mt-1.5 italic line-clamp-2">"{r.why_this}"</p>}
      </div>
    </div>
  );
};

/* ── Session list item ─────────────────────────────────────────── */
const SessionItem = ({ s, active, onClick, onDelete }) => (
  <button
    onClick={onClick}
    data-testid={`session-${s.session_id}`}
    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all group ${
      active ? 'bg-[#FF6B35]/10 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="font-medium truncate flex-1">{s.title || 'Untitled chat'}</span>
      <button onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-all"
        data-testid={`delete-session-${s.session_id}`}>
        <Trash2 className="w-3 h-3 text-red-400" />
      </button>
    </div>
    <p className="text-[10px] text-gray-400 mt-0.5">
      {s.message_count || 0} messages · {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ''}
    </p>
  </button>
);

/* ── Main Page ─────────────────────────────────────────────────── */
const AIChatPage = () => {
  const { user } = useAuth();
  const { locationString } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('fq_auth_token');
  const hdrs = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

  // State
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(searchParams.get('session') || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Fetch sessions on mount
  useEffect(() => {
    if (!token) return;
    setSessionsLoading(true);
    fetch(`${API_URL}/api/smart/chat/sessions`, { credentials: 'include', headers: hdrs })
      .then(r => r.ok ? r.json() : { sessions: [] })
      .then(d => setSessions(d.sessions || []))
      .catch(() => {})
      .finally(() => setSessionsLoading(false));
  }, [token]);

  // Load session history when activeSessionId changes
  useEffect(() => {
    if (!activeSessionId) { setMessages([]); return; }
    fetch(`${API_URL}/api/smart/chat/history/${activeSessionId}`, { credentials: 'include', headers: hdrs })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) { setMessages([]); return; }
        const rebuilt = (data.history || []).map(h => ({
          role: h.role,
          text: h.text,
          results: h.results || [],
          suggestions: h.suggestions || [],
          ts: h.ts,
        }));
        setMessages(rebuilt);
      })
      .catch(() => setMessages([]));
  }, [activeSessionId]);

  // Auto-scroll
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Auto-send query from URL params (e.g., from Surprise Me search)
  const initialQuerySent = useRef(false);
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && !initialQuerySent.current && !loading && messages.length === 0) {
      initialQuerySent.current = true;
      setInput('');
      sendMessage(q);
    }
  }, [searchParams, loading, messages.length]);

  // Send message
  const sendMessage = useCallback(async (text) => {
    const msg = (text || '').trim();
    if (!msg || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/smart/chat`, {
        method: 'POST',
        headers: hdrs,
        credentials: 'include',
        body: JSON.stringify({ message: msg, session_id: activeSessionId, location: locationString || 'your city' }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!activeSessionId) {
          setActiveSessionId(data.session_id);
          // Refresh sessions list
          setSessions(prev => [{ session_id: data.session_id, title: msg.slice(0, 60), message_count: 2, updated_at: new Date().toISOString() }, ...prev]);
        } else {
          setSessions(prev => prev.map(s => s.session_id === data.session_id ? { ...s, message_count: (s.message_count || 0) + 2, updated_at: new Date().toISOString() } : s));
        }
        setMessages(prev => [...prev, {
          role: 'ai',
          text: data.message || '',
          results: data.results || [],
          suggestions: data.suggestions || [],
          turn: data.turn,
        }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Something went wrong. Please try again.' }]);
    } finally { setLoading(false); }
  }, [activeSessionId, loading, locationString, hdrs]);

  // New chat
  const startNewChat = useCallback(() => {
    setActiveSessionId(null);
    setMessages([]);
    setInput('');
  }, []);

  // Delete session
  const deleteSession = useCallback(async (sid) => {
    await fetch(`${API_URL}/api/smart/chat/${sid}`, { method: 'DELETE', credentials: 'include', headers: hdrs });
    setSessions(prev => prev.filter(s => s.session_id !== sid));
    if (activeSessionId === sid) { setActiveSessionId(null); setMessages([]); }
  }, [activeSessionId, hdrs]);

  // Resume session
  const resumeSession = useCallback((sid) => {
    setActiveSessionId(sid);
  }, []);

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[#FAFAFA]" data-testid="ai-chat-page">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-gray-100 bg-white flex flex-col`}>
        <div className="p-4 border-b border-gray-100">
          <button onClick={startNewChat}
            className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all"
            data-testid="new-chat-btn">
            <Plus className="w-4 h-4" /> New Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {sessionsLoading ? (
            <div className="space-y-2 p-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}</div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-2" />
              <p className="text-xs text-gray-400">No conversations yet</p>
            </div>
          ) : (
            sessions.map(s => (
              <SessionItem key={s.session_id} s={s} active={activeSessionId === s.session_id}
                onClick={() => resumeSession(s.session_id)} onDelete={deleteSession} />
            ))
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
            data-testid="toggle-sidebar">
            {sidebarOpen ? <PanelLeftClose className="w-5 h-5 text-gray-500" /> : <PanelLeftOpen className="w-5 h-5 text-gray-500" />}
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Sparkles className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
            <h1 className="font-bold text-gray-900 text-base truncate">AI Food Discovery</h1>
            <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full flex-shrink-0">Multi-turn</span>
          </div>
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
            data-testid="chat-back-btn">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-5" data-testid="chat-messages-area">
          {messages.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center pt-8 md:pt-16">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B35] to-[#F4C430] rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#FF6B35]/20">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">What are you craving?</h2>
              <p className="text-sm text-gray-500 mb-8">I'll find the best picks for you. Refine as we go — I remember everything.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-w-xl mx-auto">
                {STARTERS.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)}
                    className="text-left p-3 bg-white rounded-xl text-xs text-gray-600 hover:bg-[#FFF5F0] hover:text-[#FF6B35] transition-all border border-gray-100 hover:border-[#FF6B35]/20 flex items-start gap-1.5"
                    data-testid={`starter-${i}`}>
                    <ChevronRight className="w-3 h-3 text-[#FF6B35] flex-shrink-0 mt-0.5" /><span>{s}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div className="max-w-[80%] bg-gray-900 text-white px-5 py-3 rounded-2xl rounded-br-md shadow-sm"
                    data-testid={`msg-user-${i}`}>
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  </div>
                ) : (
                  <div className="max-w-[95%] space-y-3" data-testid={`msg-ai-${i}`}>
                    {msg.text && (
                      <div className="bg-white px-5 py-3 rounded-2xl rounded-bl-md border border-gray-100 shadow-sm">
                        <p className="text-sm text-gray-700 leading-relaxed">{msg.text}</p>
                      </div>
                    )}
                    {msg.results?.length > 0 && (
                      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                        {msg.results.map((r, j) => <ChatResultCard key={j} r={r} idx={`${i}-${j}`} />)}
                      </div>
                    )}
                    {msg.suggestions?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {msg.suggestions.map((s, k) => (
                          <button key={k} onClick={() => sendMessage(s)}
                            className="text-[11px] bg-[#FFF5F0] text-[#FF6B35] hover:bg-[#FFE8D6] px-3 py-1.5 rounded-full font-medium transition-colors border border-[#FF6B35]/10"
                            data-testid={`suggestion-${i}-${k}`}>{s}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white px-5 py-3 rounded-2xl rounded-bl-md border border-gray-100 flex items-center gap-2 shadow-sm">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-400">Finding the best picks...</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-100 bg-white px-4 md:px-8 py-4">
          <div className="max-w-3xl mx-auto flex gap-3">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
              placeholder={messages.length === 0 ? "What are you craving?" : "Refine your search — e.g. 'cheaper options' or 'with outdoor seating'"}
              className="flex-1 px-5 py-3 bg-gray-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 border border-gray-200 focus:border-[#FF6B35]/30 transition-all"
              disabled={loading}
              data-testid="chat-input"
            />
            <button onClick={() => sendMessage(input)} disabled={loading || !input.trim()}
              className="px-5 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-40 transition-all active:scale-95 flex items-center gap-2 font-medium text-sm"
              data-testid="chat-send-btn">
              <Send className="w-4 h-4" /> Send
            </button>
          </div>
          {locationString && (
            <p className="text-center text-[10px] text-gray-400 mt-2 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> Searching near {locationString}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
